import React, { useState } from 'react';
import styled from 'styled-components';
import { updateCollection, deleteItem, bulkDeleteItems } from '../utils/inventoryService';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const EditorContainer = styled.div`
  padding: 20px 0;
  position: relative;
`;

const ContentContainer = styled.div`
  margin-top: 10px; /* Minimal spacing */
  padding: 0; /* Remove all padding */
  max-width: 1200px; /* Match the standard container width */
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 1100px) {
    margin-top: 10px; /* Minimal spacing */
    padding: 0 10px; /* Add padding for better spacing */
  }

  @media (max-width: 768px) {
    margin-top: 10px; /* Minimal spacing */
    padding: 0 10px; /* Add padding on mobile for better spacing */
  }
`;

const CollectionTitle = styled.h2`
  font-size: 1.5rem;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
`;

const CollectionDetails = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 30px;
  margin-top: 0;
  margin-bottom: 10px; /* Reduced from 30px to reduce space below */
  background: rgba(255, 255, 255, 0.05);
  padding: 20px 20px 20px 20px;
  border-radius: 0; /* Remove rounded corners at top */
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 200px;
`;

const DetailLabel = styled.span`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 3px; /* Minimal space between label and input */
`;

const DetailValue = styled.input`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 1rem;
  color: #fff;
  margin-top: 5px;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

// Removing unused styled component

const ItemsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 30px;
  width: 100%;
  box-sizing: border-box;
`;

const ItemCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
`;

const ItemImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemDetails = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ItemTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 5px;
`;

const ItemAvailabilityBadge = styled.div`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: bold;
  background-color: ${props => props.available ? '#2c7d46' : '#a00'};
  color: white;
  margin-bottom: 10px;
`;

const ItemField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
`;

const ItemLabel = styled.label`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 5px;
`;

const ItemInput = styled.input`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.9rem;
  color: #fff;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const ItemSelect = styled.select`
  background: #000;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.9rem;
  color: #fff;

  &:focus {
    outline: none;
    border-color: #2c7d46;
  }
`;

const ItemStatusInput = styled.input`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.9rem;
  color: #fff;
  margin-top: 5px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #a00;
  }
`;

// Removing unused styled components for site header

const EditorHeader = styled.div`
  padding: 40px 0 0 0; /* Add padding above title to prevent overlap with nav bar */
  background-color: #000; /* Match exact page background */
  z-index: 90;
  width: 100%;
  max-width: 1200px; /* Match the standard container width */
  margin: 0 auto; /* Center the header */
`;

const StickyHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 990; /* High enough to be above content but below the main header */
  background-color: #000; /* Match exact page background */
  display: flex;
  justify-content: center;
  min-height: 50px; /* Minimum height instead of fixed height */
  padding: 5px 0; /* Add padding to ensure content is visible */
  margin-top: 100px; /* Add margin to position below the header */

  @media (max-width: 1100px) {
    margin-top: 150px; /* Increase margin for the responsive header */
  }

  @media (max-width: 768px) {
    margin-top: 180px; /* Further increase margin for mobile */
  }
`;

const EditorControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px 0 10px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    div {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #aaa;
  color: #fff;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 10px;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 8px 15px;
    font-size: 0.9rem;
    height: 36px;
    min-width: 100px;
  }
`;

const SaveButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-left: 10px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #36964f;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    margin-left: 10px;
    padding: 8px 15px;
    font-size: 0.9rem;
    height: 36px;
    min-width: 100px;
  }
`;

const DeleteItemButton = styled.button`
  background: #cc0000;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 15px;

  &:hover {
    background: #990000;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const BulkDeleteButton = styled.button`
  background: #cc0000;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-left: 10px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #990000;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    margin-left: 10px;
    padding: 8px 15px;
    font-size: 0.9rem;
    height: 36px;
    min-width: 100px;
  }
`;

const SuccessMessage = styled.div`
  background: rgba(44, 125, 70, 0.2);
  border: 1px solid #2c7d46;
  color: #2c7d46;
  padding: 10px 20px;
  border-radius: 4px;
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 200;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const BulkEditModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 300;
`;

const BulkEditContent = styled.div`
  background-color: #111;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
`;

const BulkEditHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BulkEditTitle = styled.h2`
  margin: 0;
`;

const BulkEditClose = styled.button`
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

const BulkEditControls = styled.div`
  margin: 20px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const BulkEditItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`;

const BulkEditItem = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid ${props => props.selected ? '#2c7d46' : 'transparent'};

  &:hover {
    opacity: 0.9;
  }
`;

const BulkEditItemImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
`;

const BulkEditItemCheckmark = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background-color: #2c7d46;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const BulkEditField = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
`;

const BulkEditFieldHeader = styled.h3`
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.1rem;
`;

const BulkEditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

function InventoryEditor({ collection, onSave, onBack }) {
  const [collectionData, setCollectionData] = useState(collection);
  const [items, setItems] = useState(collection.items || []);
  const [isSaved, setIsSaved] = useState(false);
  const [savingError, setSavingError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Bulk Edit state
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkEditData, setBulkEditData] = useState({
    medium: '',
    size: '',
    available: 'nochange',
    status: ''
  });

  // Delete state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteItemIndex, setDeleteItemIndex] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Helper to get the image path for an item
  const getImagePath = (item) => {
    if (!item) {
      console.warn('Missing item');
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    }

    // Items can have either 'image' or 'imagePath' property
    const imagePath = item.image || item.imagePath;

    if (!imagePath) {
      console.warn('Missing image path for item:', item.id);
      return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    }

    // Check if the image path already includes the folder name (full path)
    if (imagePath.includes('/')) {
      // If it's already a full path, use it directly
      console.log(`📸 Using full path for ${item.id}: ${imagePath}`);
      return imagePath;
    }

    // For standard uploaded images, they follow the pattern (01.jpg, 02.jpg, etc.)
    // We need to use the correct path structure
    const folderName = collection.folder || collection.id;
    const fullPath = `/images/collections/${folderName}/${imagePath}`;
    console.log(`📸 Generated path for ${item.id}: ${fullPath}`);
    return fullPath;
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];

    if (field === 'available') {
      value = value === 'true'; // Convert string to boolean
    } else if (field === 'price' && value !== '') {
      value = parseFloat(value);
    }

    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    setItems(updatedItems);
    setHasChanges(true);
  };

  const handleCollectionChange = (field, value) => {
    if (field === 'price' && value !== '') {
      value = parseFloat(value);
    }

    setCollectionData({
      ...collectionData,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    try {
      setSavingError(''); // Clear any previous errors
      console.log('💾 Starting save...');

      const updatedCollection = {
        ...collectionData,
        items: items
      };

      // Save the changes using the inventory service
      const success = await updateCollection(updatedCollection);

      if (success) {
        console.log('✅ Save successful, updating UI...');
        setIsSaved(true);
        setHasChanges(false);
        setTimeout(() => setIsSaved(false), 3000);

        // Call onSave callback to refresh parent component
        if (onSave) {
          console.log('🔄 Triggering parent refresh...');
          onSave(updatedCollection);
        }
      } else {
        throw new Error('Failed to update collection');
      }
    } catch (error) {
      console.error('❌ Error saving data:', error);
      setSavingError('Failed to save changes. Please try again.');
    }
  };

  // Bulk edit helpers
  const toggleItemSelection = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const selectAllItems = () => {
    setSelectedItems(items.map(item => item.id));
  };

  const deselectAllItems = () => {
    setSelectedItems([]);
  };

  const handleBulkEditChange = (field, value) => {
    setBulkEditData({
      ...bulkEditData,
      [field]: value
    });
  };

  const applyBulkEdit = () => {
    const updatedItems = items.map(item => {
      if (selectedItems.includes(item.id)) {
        const updatedItem = { ...item };

        // Only update fields that were changed
        if (bulkEditData.medium) {
          updatedItem.medium = bulkEditData.medium;
        }

        if (bulkEditData.size) {
          updatedItem.size = bulkEditData.size;
        }

        if (bulkEditData.available !== 'nochange') {
          updatedItem.available = bulkEditData.available === 'true';

          // If marking as not available and status is provided, update status
          if (bulkEditData.available === 'false' && bulkEditData.status) {
            updatedItem.status = bulkEditData.status;
          }
        }

        return updatedItem;
      }
      return item;
    });

    setItems(updatedItems);
    setHasChanges(true);
    setBulkEditMode(false);
    setSelectedItems([]);
    setBulkEditData({
      medium: '',
      size: '',
      available: 'nochange',
      status: ''
    });

    // Show success message
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };

  // Delete handlers
  const handleDeleteItem = (index, item) => {
    // Check if item has been purchased
    if (item.purchasedDate) {
      alert(`Cannot delete purchased item.\n\nThis item was purchased on ${new Date(item.purchasedDate).toLocaleDateString()}. Purchased items cannot be deleted.`);
      return;
    }

    setItemToDelete(item);
    setDeleteItemIndex(index);
    setIsBulkDelete(false);
    setDeleteError(null);
    setDeleteModalVisible(true);
  };

  const handleBulkDelete = () => {
    // Get selected items
    const selectedItemObjects = items.filter(item => selectedItems.includes(item.id));

    // Check if any are purchased
    const purchasedItems = selectedItemObjects.filter(item => item.purchasedDate);

    if (purchasedItems.length > 0) {
      const purchasedNames = purchasedItems.map(item => item.name || item.title || item.id).join(', ');
      alert(`Cannot delete purchased items.\n\nThe following ${purchasedItems.length} item(s) have been purchased and cannot be deleted:\n${purchasedNames}`);
      return;
    }

    setItemToDelete(selectedItemObjects);
    setIsBulkDelete(true);
    setDeleteError(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      if (isBulkDelete) {
        // Bulk delete
        const itemIds = selectedItems;
        const result = await bulkDeleteItems(collectionData.folder, itemIds);

        if (result.success) {
          // Remove deleted items from state
          const deletedIds = result.deletedItems.map(item => item.id);
          const updatedItems = items.filter(item => !deletedIds.includes(item.id));
          setItems(updatedItems);

          // Clear selections
          setSelectedItems([]);
          setBulkEditMode(false);

          console.log(`✅ Successfully deleted ${result.deletedItems.length} items`);

          // Call onSave to refresh parent component
          if (onSave) {
            onSave({
              ...collectionData,
              items: updatedItems
            });
          }
        }
      } else {
        // Single item delete
        const result = await deleteItem(collectionData.folder, itemToDelete.id);

        if (result.success) {
          // Remove item from state
          const updatedItems = items.filter((_, idx) => idx !== deleteItemIndex);
          setItems(updatedItems);

          console.log(`✅ Successfully deleted item: ${itemToDelete.id}`);

          // Call onSave to refresh parent component
          if (onSave) {
            onSave({
              ...collectionData,
              items: updatedItems
            });
          }
        }
      }

      // Close modal
      setDeleteModalVisible(false);
      setItemToDelete(null);
      setDeleteItemIndex(null);
      setIsBulkDelete(false);

    } catch (error) {
      console.error('Error deleting item(s):', error);
      setDeleteError(error.message || 'Failed to delete item(s)');
      // Don't close modal on error so user can see the error and retry
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setItemToDelete(null);
    setDeleteItemIndex(null);
    setIsBulkDelete(false);
    setDeleteError(null);
  };

  return (
    <EditorContainer>
      {/* Fixed Header */}
      <StickyHeader>
        {/* Editor Controls */}
        <EditorHeader>
          <EditorControls>
            <CollectionTitle>Edit: {collection.title}</CollectionTitle>
            <div>
              <ActionButton onClick={onBack}>← Back to Collections</ActionButton>
              <SaveButton onClick={handleSubmit} disabled={!hasChanges}>Save Changes</SaveButton>
            </div>
          </EditorControls>
        </EditorHeader>
      </StickyHeader>

      {/* Scrollable Content */}
      <ContentContainer>
        <CollectionDetails>
          <DetailItem>
            <DetailLabel>Collection Title</DetailLabel>
            <DetailValue
              type="text"
              value={collectionData.title}
              onChange={(e) => handleCollectionChange('title', e.target.value)}
            />
          </DetailItem>
          <DetailItem>
            <DetailLabel>Year</DetailLabel>
            <DetailValue
              type="text"
              value={collectionData.year || ''}
              onChange={(e) => handleCollectionChange('year', e.target.value)}
            />
          </DetailItem>
          <DetailItem style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <SaveButton
              onClick={() => setBulkEditMode(true)}
              style={{ marginBottom: '5px' }}
            >
              Bulk Edit Items
            </SaveButton>
          </DetailItem>
        </CollectionDetails>

        <h3 style={{ marginTop: '3px' }}>Individual Items ({items.length})</h3>

        <ItemsList>
        {items.map((item, index) => (
          <ItemCard key={item.id}>
            <ItemImageContainer>
              <ItemImage
                src={getImagePath(item)}
                alt={item.title}
                onError={(e) => {
                  console.error(`❌ Failed to load image for ${item.id}: ${e.target.src}`);
                  // Prevent infinite loop - disable error handler immediately
                  e.target.onerror = null;
                  // Set a light gray background so we can see there was an error
                  e.target.style.backgroundColor = '#333';
                  e.target.style.display = 'flex';
                  e.target.style.alignItems = 'center';
                  e.target.style.justifyContent = 'center';
                  // Clear the src to stop trying to load
                  e.target.src = '';
                }}
              />
            </ItemImageContainer>
            
            <ItemDetails>
              <ItemTitle>{item.title}</ItemTitle>
              <ItemAvailabilityBadge available={item.available}>
                {item.available ? 'Available' : item.status || 'Not Available'}
              </ItemAvailabilityBadge>
              
              <ItemField>
                <ItemLabel>Title</ItemLabel>
                <ItemInput 
                  type="text" 
                  value={item.title} 
                  onChange={(e) => handleItemChange(index, 'title', e.target.value)} 
                />
              </ItemField>
              
              <ItemField>
                <ItemLabel>Price ($)</ItemLabel>
                <ItemInput 
                  type="number" 
                  value={item.price || ''} 
                  onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                  disabled={!item.available} 
                />
              </ItemField>
              
              <ItemField>
                <ItemLabel>Availability</ItemLabel>
                <ItemSelect 
                  value={item.available.toString()} 
                  onChange={(e) => handleItemChange(index, 'available', e.target.value)}
                >
                  <option value="true">Available</option>
                  <option value="false">Not Available</option>
                </ItemSelect>
              </ItemField>
              
              {!item.available && (
                <ItemField>
                  <ItemLabel>Status</ItemLabel>
                  <ItemStatusInput
                    type="text"
                    value={item.status || ''}
                    onChange={(e) => handleItemChange(index, 'status', e.target.value)}
                    placeholder="SOLD, PRIVATE COLLECTION, etc."
                  />
                </ItemField>
              )}

              <ItemField>
                <ItemLabel>Medium</ItemLabel>
                <ItemSelect
                  value={item.medium || ''}
                  onChange={(e) => handleItemChange(index, 'medium', e.target.value)}
                >
                  <option value="">Select a medium</option>
                  <option value="Encaustic">Encaustic</option>
                  <option value="Acrylic on Canvas">Acrylic on Canvas</option>
                  <option value="Acrylic on Paper">Acrylic on Paper</option>
                  <option value="Acrylic Pen on Paper">Acrylic Pen on Paper</option>
                  <option value="Acrylic Pen on Cloth">Acrylic Pen on Cloth</option>
                </ItemSelect>
              </ItemField>

              <ItemField>
                <ItemLabel>Size</ItemLabel>
                <ItemInput
                  type="text"
                  value={item.size || ''}
                  onChange={(e) => handleItemChange(index, 'size', e.target.value)}
                />
              </ItemField>

              <DeleteItemButton
                onClick={() => handleDeleteItem(index, item)}
                disabled={item.purchasedDate}
                title={item.purchasedDate ? 'Cannot delete purchased items' : 'Delete this item'}
              >
                🗑️ Delete Item
              </DeleteItemButton>
            </ItemDetails>
          </ItemCard>
        ))}
      </ItemsList>

        {savingError && (
          <div style={{ color: 'red', marginTop: '20px' }}>
            {savingError}
          </div>
        )}
      </ContentContainer>

      {isSaved && (
        <SuccessMessage>
          ✅ Changes saved successfully!
        </SuccessMessage>
      )}

      {/* Bulk Edit Modal */}
      {bulkEditMode && (
        <BulkEditModal>
          <BulkEditContent>
            <BulkEditHeader>
              <BulkEditTitle>Bulk Edit Items</BulkEditTitle>
              <BulkEditClose onClick={() => {
                setBulkEditMode(false);
                setSelectedItems([]);
              }}>×</BulkEditClose>
            </BulkEditHeader>

            <div>
              <p>Select items to edit ({selectedItems.length} of {items.length} selected)</p>
              <BulkEditControls>
                <ActionButton onClick={selectAllItems}>Select All</ActionButton>
                <ActionButton onClick={deselectAllItems}>Deselect All</ActionButton>
              </BulkEditControls>
            </div>

            <BulkEditItemsGrid>
              {items.map(item => (
                <BulkEditItem
                  key={item.id}
                  selected={selectedItems.includes(item.id)}
                  onClick={() => toggleItemSelection(item.id)}
                >
                  <BulkEditItemImage
                    src={getImagePath(item)}
                    alt={item.title}
                    onError={(e) => {
                      // Prevent infinite loop - disable error handler immediately
                      e.target.onerror = null;
                      // Set a light gray background so we can see there was an error
                      e.target.style.backgroundColor = '#333';
                      // Clear the src to stop trying to load
                      e.target.src = '';
                    }}
                  />
                  {selectedItems.includes(item.id) && (
                    <BulkEditItemCheckmark>✓</BulkEditItemCheckmark>
                  )}
                </BulkEditItem>
              ))}
            </BulkEditItemsGrid>

            <BulkEditField>
              <BulkEditFieldHeader>Medium</BulkEditFieldHeader>
              <ItemSelect
                value={bulkEditData.medium}
                onChange={(e) => handleBulkEditChange('medium', e.target.value)}
              >
                <option value="">No change</option>
                <option value="Encaustic">Encaustic</option>
                <option value="Acrylic on Canvas">Acrylic on Canvas</option>
                <option value="Acrylic on Paper">Acrylic on Paper</option>
                <option value="Acrylic Pen on Paper">Acrylic Pen on Paper</option>
                <option value="Acrylic Pen on Cloth">Acrylic Pen on Cloth</option>
              </ItemSelect>
            </BulkEditField>

            <BulkEditField>
              <BulkEditFieldHeader>Size</BulkEditFieldHeader>
              <ItemInput
                type="text"
                value={bulkEditData.size}
                onChange={(e) => handleBulkEditChange('size', e.target.value)}
                placeholder='e.g. 24" x 36"'
              />
            </BulkEditField>

            <BulkEditField>
              <BulkEditFieldHeader>Availability</BulkEditFieldHeader>
              <ItemSelect
                value={bulkEditData.available}
                onChange={(e) => handleBulkEditChange('available', e.target.value)}
              >
                <option value="nochange">No change</option>
                <option value="true">Available</option>
                <option value="false">Not Available</option>
              </ItemSelect>
            </BulkEditField>

            {bulkEditData.available === 'false' && (
              <BulkEditField>
                <BulkEditFieldHeader>Status Message</BulkEditFieldHeader>
                <ItemInput
                  type="text"
                  value={bulkEditData.status}
                  onChange={(e) => handleBulkEditChange('status', e.target.value)}
                  placeholder="SOLD, PRIVATE COLLECTION, etc."
                />
              </BulkEditField>
            )}

            <BulkEditActions>
              <ActionButton onClick={() => {
                setBulkEditMode(false);
                setSelectedItems([]);
              }}>Cancel</ActionButton>
              <SaveButton
                onClick={applyBulkEdit}
                disabled={selectedItems.length === 0}
              >
                Apply Changes to {selectedItems.length} Items
              </SaveButton>
              <BulkDeleteButton
                onClick={handleBulkDelete}
                disabled={selectedItems.length === 0}
              >
                🗑️ Delete {selectedItems.length} Items
              </BulkDeleteButton>
            </BulkEditActions>
          </BulkEditContent>
        </BulkEditModal>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        item={!isBulkDelete ? itemToDelete : null}
        items={isBulkDelete ? itemToDelete : null}
        isBulk={isBulkDelete}
        itemCount={isBulkDelete ? selectedItems.length : 1}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </EditorContainer>
  );
}

export default InventoryEditor;